import React from 'react';
import PropTypes from 'prop-types';
import Router from 'next/router';
import { makeStyles } from '@material-ui/core/styles';
import Link from 'next/link';
import { observer } from 'mobx-react';
import withStores from '@studionand/ada-next/src/stores/withStores';
import Box from '@material-ui/core/Box';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import Slide from '@material-ui/core/Slide';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MuiDivider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Hidden from '@material-ui/core/Hidden';
import InputAdornment from '@material-ui/core/InputAdornment';
import AvatarIcon from '../../../shared/icons/Avatar';
import SearchIcon from '../../../shared/icons/Search';
import CloseIcon from '../../../shared/icons/Close';
import NavSection from './NavSection';
import MobileNavSection from './MobileNavSection';
import CONFIG from './constants.js';

function HideOnScroll(props) {
  const { children } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: global.window ? window : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    position: 'fixed',
    height: theme.spacing(10),
    width: '100%',
    display: 'flex',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  logo: {
    cursor: 'pointer',
  },
  divider: {
    margin: theme.spacing(0, 3),
  },
  searchInput: {
    minWidth: 250,
    width: '100%',
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  input: {
    fontSize: 20,
  },
  adornment: {
    height: '100%',
  },
  iconDivider: {
    width: 1,
    height: theme.spacing(4),
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  link: {
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  navigation: {
    position: 'fixed',
    zIndex: 11,
    top: theme.spacing(10),
    width: '100%',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      height: `calc(100vh - ${theme.spacing(10)}px)`,
    },
  },
  mobileSearchContainer: {
    position: 'fixed',
    zIndex: 2,
    top: theme.spacing(10),
    width: '100%',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const Divider = props => {
  const classes = useStyles();
  return (
    <React.Fragment>
      <Hidden smDown>
        <MuiDivider
          className={classes.divider}
          orientation="vertical"
          flexItem
          {...props}
        />
      </Hidden>
      <Hidden mdUp>
        <Box ml={0.5} mr={0.5} />
      </Hidden>
    </React.Fragment>
  );
};

export const SearchInput = props => {
  const { onSubmit, ...rest } = props;
  const classes = useStyles();
  return (
    <TextField
      className={classes.searchInput}
      label=""
      placeholder="Suche"
      InputProps={{
        className: classes.input,
        disableUnderline: true,
        endAdornment: (
          <InputAdornment position="end">
            <div className={classes.iconDivider} />
            <SearchIcon className={classes.searchIcon} onClick={onSubmit} />
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
};

SearchInput.propTypes = {
  onSubmit: PropTypes.func,
};

SearchInput.defaultProps = {
  onSubmit: () => {},
};

const Header = props => {
  const { loginStore } = props;
  const classes = useStyles();

  const [isOpen, setIsOpen] = React.useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const linkContext = process.env.NODE_ENV === 'production' ? '/' : '/depi/';

  const handleChangeQuery = e => {
    setQuery(e.target.value);
  };

  const handleSubmitSearch = e => {
    if (query.length !== 0 && e.keyCode === 13)
      Router.push(`${linkContext}suche?q=${query}`);
  };

  const handleToggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleCloseMenu = () => {
    setIsOpen(false);
  };

  const handleToggleMobileSearch = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
  };

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
  });

  React.useEffect(() => {
    const routeChangeComplete = () => {
      setIsOpen(false);
    };

    Router.events.on('routeChangeComplete', routeChangeComplete);
    return () => {
      Router.events.off('routeChangeComplete', routeChangeComplete);
    };
  }, []);

  return (
    <React.Fragment>
      <HideOnScroll>
        <header className={classes.root}>
          <Link href={process.env.NODE_ENV === 'production' ? '/' : '/depi'}>
            <img
              className={classes.logo}
              src="/images/DEPI_Logo.svg"
              height="100%"
              width="auto"
            />
          </Link>
          <Box display="flex" height="100%" alignItems="center">
            <Divider />
            <Hidden smDown>
              <SearchInput
                value={query}
                onChange={handleChangeQuery}
                onKeyDown={handleSubmitSearch}
                onSubmit={handleSubmitSearch}
              />
            </Hidden>
            <Hidden mdUp>
              <Button variant="text" onClick={handleToggleMobileSearch}>
                {isMobileSearchOpen ? <CloseIcon /> : <SearchIcon />}
              </Button>
            </Hidden>
            <Divider />
            <Button variant="text" size="large" onClick={handleToggleMenu}>
              {isOpen ? <CloseIcon /> : <b>Menü</b>}
            </Button>
            <Divider />
            <Link href={`${linkContext}anmelden`} passHref>
              {loginStore.isLoggedIn ? (
                <IconButton>
                  <AvatarIcon />
                </IconButton>
              ) : (
                <Typography component="a" className={classes.link} variant="h6">
                  Login
                </Typography>
              )}
            </Link>
          </Box>
        </header>
      </HideOnScroll>
      {isOpen && (
        <React.Fragment>
          <Box className={classes.navigation}>
            <Hidden smDown>
              <Box display="flex" justifyContent="space-between">
                {CONFIG.map(section => (
                  <NavSection
                    key={section.title || section[0].title}
                    section={section}
                  />
                ))}
              </Box>
            </Hidden>
            <Hidden mdUp>
              <Box display="flex" flexDirection="column" width="100%" flexGrow={1} style={{overflowY: 'auto'}}>
                {CONFIG.map(section => (
                  <MobileNavSection
                    key={section.title || section[0].title}
                    section={section}
                  />
                ))}
              </Box>
            </Hidden>
            <Box mt={2}>
              <Button variant="text" onClick={handleCloseMenu}>
                Menü schließen
              </Button>
            </Box>
          </Box>
        </React.Fragment>
      )}
      {isMobileSearchOpen && (
        <Hidden mdUp>
          <HideOnScroll>
            <Box className={classes.mobileSearchContainer}>
              <SearchInput
                value={query}
                onChange={handleChangeQuery}
                onKeyDown={handleSubmitSearch}
              />
            </Box>
          </HideOnScroll>
        </Hidden>
      )}
      <Box height={80} />
    </React.Fragment>
  );
};

export default withStores('loginStore')(observer(Header));
